import React, { useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Typography,
  TextField,
  Autocomplete,
  Popover,
  IconButton,
  Box,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { IoMdClose } from "react-icons/io";
import { MdEdit, MdFileCopy } from "react-icons/md";
import { FaCirclePlay } from "react-icons/fa6";
import AddIcon from "@mui/icons-material/Add";
import JsonViewer from "./CustomJsonViewerEditor";
import CustomTextEditor from "./CustomInputTextEditor";

const SlideTransition = (props) => <Slide {...props} direction="up" />;

const AlertMessage = ({ open, onClose, message, severity, title }) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        onClose={onClose}
        severity={severity || "info"}
        sx={{ width: "100%" }}
        variant="filled"
      >
        <strong>{title}</strong>
        <br />
        {message}
      </Alert>
    </Snackbar>
  );
};

const safeParseObj = (str, fallback = {}) => {
  try {
    if (typeof str === "string") {
      if (!str.trim()) return fallback;
      const v = JSON.parse(str);
      return v && typeof v === "object" && !Array.isArray(v) ? v : fallback;
    }
    return str && typeof str === "object" && !Array.isArray(str) ? str : fallback;
  } catch {
    return fallback;
  }
};

const pretty = (obj) => JSON.stringify(obj ?? {}, null, 2);

const EnvEditor = ({ value, onChange, disabled = false }) => {
  const initialRows = useMemo(() => {
    const envObj = safeParseObj(value, {});
    return Object.entries(envObj).map(([k, v]) => ({
      key: k,
      value: v ?? "",
    }));
  }, []);

  const [rows, setRows] = useState(initialRows);

  useEffect(() => {
    const envObj = safeParseObj(value, {});
    const nextRows = Object.entries(envObj).map(([k, v]) => ({
      key: k,
      value: v ?? "",
    }));
    setRows(nextRows);
  }, [value]);

  const commit = (nextRows) => {
    setRows(nextRows);
    const obj = nextRows.reduce((acc, { key, value }) => {
      const k = (key ?? "").trim();
      if (!k) return acc;
      acc[k] = value;
      return acc;
    }, {});
    onChange(pretty(obj));
  };

  const addRow = () => commit([...rows, { key: "", value: "" }]);
  const removeRow = (idx) => commit(rows.filter((_, i) => i !== idx));
  const updateRow = (idx, field, val) =>
    commit(rows.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));

  return (
    <Box mt={1}>
      <Typography sx={{ color: "white", mb: 1, fontSize: 13, opacity: 0.9 }}>
        Environment Variables
      </Typography>
      {rows.map((row, idx) => (
        <Box
          key={idx}
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ mb: 1 }}
        >
          <TextField
            label="Key"
            size="small"
            value={row.key}
            disabled={disabled}
            onChange={(e) => updateRow(idx, "key", e.target.value)}
            InputLabelProps={{ style: { color: "white" } }}
            InputProps={{ style: { color: "white" } }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#4d35ff" },
                "&:hover fieldset": { borderColor: "#4d35ff" },
                "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
              },
            }}
          />
          <TextField
            label="Value"
            size="small"
            value={row.value}
            disabled={disabled}
            onChange={(e) => updateRow(idx, "value", e.target.value)}
            InputLabelProps={{ style: { color: "white" } }}
            InputProps={{ style: { color: "white" } }}
            sx={{
              flex: 2,
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#4d35ff" },
                "&:hover fieldset": { borderColor: "#4d35ff" },
                "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
              },
            }}
          />
          <IconButton
            onClick={() => removeRow(idx)}
            disabled={disabled}
            title="Remove"
            style={{ color: "white", padding: 6 }}
          >
            <IoMdClose size={16} />
          </IconButton>
        </Box>
      ))}
      <Button
        type="button"
        variant="outlined"
        size="small"
        onClick={addRow}
        disabled={disabled}
        sx={{ color: "white", borderColor: "#4d35ff" }}
      >
        Add variable
      </Button>
      <Box
        mt={1.5}
        sx={{
          border: "1px dashed #4d35ff",
          borderRadius: 1,
          p: 1,
          color: "#ddd",
          fontSize: 11,
          whiteSpace: "pre-wrap",
          bgcolor: "#111326",
        }}
      >
        {pretty(
          rows.reduce((acc, { key, value }) => {
            const k = (key ?? "").trim();
            if (!k) return acc;
            acc[k] = value;
            return acc;
          }, {})
        )}
      </Box>
    </Box>
  );
};

const CustomNode = ({ data, id }) => {
  return (
    <div
      style={{
        background: "#2A2C4B",
        border: data.status === "running" ? "2px solid #ff9800" : "1px solid #4d35ff",
        borderRadius: "8px",
        padding: "8px",
        width: "200px",
        color: "white",
        position: "relative",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "#4d35ff", width: "8px", height: "8px" }}
      />
      <Typography style={{ fontWeight: "bold", fontSize: "12px" }}>
        {data.name}
        {data.status === "running" && (
          <CircularProgress size={12} sx={{ ml: 1, color: "#ff9800" }} />
        )}
      </Typography>
      <Typography style={{ fontWeight: "bold", fontSize: "10px" }}>
        Service: {data?.service || "None"}
      </Typography>
      {data.service === "Add Mcp" && (
        <>
          <Typography style={{ fontWeight: "bold", fontSize: "10px" }}>
            Transport: {data?.transport || "None"}
          </Typography>
          <Typography style={{ fontWeight: "bold", fontSize: "10px" }}>
            Command: {data?.command || "None"}
          </Typography>
          <Typography
            style={{ fontSize: "10px", color: "#ccc", wordBreak: "break-all" }}
          >
            Args: {data.args?.join(", ") || "None"}
          </Typography>
          <Typography
            style={{ fontSize: "10px", color: "#ccc", wordBreak: "break-all" }}
          >
            Env:{" "}
            {data.env && Object.keys(data.env).length
              ? JSON.stringify(data.env)
              : "None"}
          </Typography>
        </>
      )}
      {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <input
          type="checkbox"
          checked={data.thinkMode || false}
          onChange={(e) => data.onThinkModeChange(id, e.target.checked)}
          style={{ cursor: "pointer" }}
        />
        <Typography style={{ fontSize: "10px", color: "#ccc" }}>
          Think Mode (Enhances result accuracy, returns text only)
        </Typography>
      </div> */}
      <div
        style={{
          position: "absolute",
          top: "5px",
          right: "5px",
          display: "flex",
          gap: "5px",
          justifyContent: "end",
        }}
      >
        <IconButton
          type="button"
          onClick={() => data.onTest(data.path)}
          style={{ color: "white", padding: 0 }}
          disabled={!data?.isData || data.status === "running"}
        >
          <FaCirclePlay size={14} color={data?.isData && data.status !== "running" ? "#4d35ff" : "#fff"} />
        </IconButton>
        <IconButton
          onClick={(event) => data.onEditClick(event, id)}
          style={{ color: "white", padding: 0 }}
          disabled={data.status === "running"}
        >
          <MdEdit size={14} />
        </IconButton>
        <IconButton
          onClick={() => data.onCopy(data.path)}
          style={{ color: "white", padding: 0 }}
          disabled={data.status === "running"}
        >
          <MdFileCopy size={14} />
        </IconButton>
        {data.path.length > 0 && (
          <IconButton
            onClick={() => data.onRemove(id)}
            style={{ color: "white", padding: 0 }}
            disabled={data.status === "running"}
          >
            <IoMdClose size={14} />
          </IconButton>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "#4d35ff", width: "8px", height: "8px" }}
      />
    </div>
  );
};

const nodeTypes = { custom: CustomNode };

export function schemaToEndParamsFlat(schema, options = {}) {
  const {
    preferredDefaults = {},
    useSchemaDefault = true,
    inferFromDescription = true,
    fallback = "",
    includeMissingRequired = true,
    keyStrategy = "leaf",
    collision = "first",
  } = options;

  const PRIMS = new Set(["string", "number", "boolean"]);
  const out = {};

  const leafKeyFromPath = (fullPath) =>
    fullPath.replace(/\[\]/g, "").split(".").pop();

  const applyCollision = (k, v) => {
    if (!(k in out)) { out[k] = v; return; }
    if (collision === "first") return;
    if (collision === "last") { out[k] = v; return; }
    if (collision === "array") {
      const prev = out[k];
      out[k] = Array.isArray(prev) ? [...prev, v] : [prev, v];
      return;
    }
    if (collision === "suffix") {
      let i = 2, kk = `${k}_${i}`;
      while (kk in out) { i += 1; kk = `${k}_${i}`; }
      out[kk] = v;
    }
  };

  const inferFromDesc = (desc, t) => {
    if (!inferFromDescription || typeof desc !== "string") return undefined;
    let m = desc.match(/Example:\s*"([^"]+)"/i);
    if (m && m[1] != null) return castToken(m[1], t);
    m = desc.match(/Example:\s*([^\s,.;]+)/i);
    if (m && m[1] != null) return castToken(m[1], t);
    return undefined;
  };

  const castToken = (token, t) => {
    if (t === "number" && !isNaN(Number(token))) return Number(token);
    if (t === "boolean" && /^(true|false)$/i.test(token)) return /^true$/i.test(token);
    return token;
  };

  const pickDefault = (fullPath, node, t) => {
    const leafKey = leafKeyFromPath(fullPath);
    if (Object.prototype.hasOwnProperty.call(preferredDefaults, fullPath)) {
      return preferredDefaults[fullPath];
    }
    if (Object.prototype.hasOwnProperty.call(preferredDefaults, leafKey)) {
      return preferredDefaults[leafKey];
    }
    if (useSchemaDefault && node && Object.prototype.hasOwnProperty.call(node, "default")) {
      return node.default;
    }
    const inferred = inferFromDesc(node?.description, t);
    if (inferred !== undefined) return inferred;
    if (t === "number") return fallback === "" ? "" : fallback;
    if (t === "boolean") return fallback === "" ? "" : fallback;
    return fallback;
  };

  const emit = (fullPath, node, t) => {
    const key = keyStrategy === "leaf" ? leafKeyFromPath(fullPath) : fullPath;
    const val = pickDefault(fullPath, node, t);
    applyCollision(key, val);
  };

  const addMissingRequired = (node, path) => {
    if (!includeMissingRequired) return;
    if (!node || !Array.isArray(node.required)) return;
    const props = node.properties && typeof node.properties === "object" ? node.properties : {};
    for (const reqKey of node.required) {
      if (!props[reqKey]) {
        const reqPath = path ? `${path}.${reqKey}` : reqKey;
        emit(reqPath, {}, "string");
      }
    }
  };

  const visit = (node, path) => {
    if (!node || typeof node !== "object") return;
    const nodeType = Array.isArray(node.type) ? node.type[0] : node.type;
    if (nodeType === "object" || (node.properties && typeof node.properties === "object")) {
      const props = node.properties || {};
      for (const [name, prop] of Object.entries(props)) {
        const nextPath = path ? `${path}.${name}` : name;
        visit(prop, nextPath);
      }
      addMissingRequired(node, path);
      return;
    }
    if (nodeType === "array" || node.items) {
      const items = node.items || {};
      const itemType = Array.isArray(items.type) ? items.type[0] : items.type;
      if (PRIMS.has(String(itemType).toLowerCase())) {
        const arrPath = `${path}[]`;
        const t = String(itemType).toLowerCase();
        emit(arrPath, items, t);
      } else {
        visit(items, `${path}[]`);
      }
      return;
    }
    const t = String(nodeType || "").toLowerCase();
    if (PRIMS.has(t)) {
      emit(path, node, t);
    }
  };

  visit(schema, "");
  return out;
};

function limitArrayElements(input, limit = 4, seen = new WeakSet()) {
  if (input == null) return input;
  if (typeof input === "object" && input !== null) {
    if (seen.has(input)) return null;
    seen.add(input);
  }
  if (Array.isArray(input)) {
    return input
      .slice(0, limit)
      .map((item) => limitArrayElements(item, limit, seen));
  }
  if (typeof input === "object") {
    const result = {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        result[key] = limitArrayElements(input[key], limit, seen);
      }
    }
    return result;
  }
  return input;
}

const postJson = async (url, body) => {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}. Please check your server connection or API endpoint configuration.`);
    }
    const result = await res.json();
    if (result.status === false) {
      throw new Error(
        result.error || 
        "Operation failed. Please verify your environment variables and arguments, or check the service configuration."
      );
    }
    return result;
  } catch (error) {
    throw error;
  }
};

const buildEffectiveConfig = (services, state) => {
  const serviceCfg = services?.find((s) => s.service === state.service)?.tool_config || null;
  const envFromEditor =
    typeof state.env === "string"
      ? safeParseObj(state.env, {})
      : state.env || {};

  if (state.service === "Add Mcp") {
    return {
      transport: state.transport || "stdio",
      command: state.command || "npx",
      args: Array.isArray(state.args) ? state.args : [],
      env: envFromEditor,
    };
  }

  if (serviceCfg) {
    return {
      transport: serviceCfg.transport || "stdio",
      command: serviceCfg.command || "npx",
      args: Array.isArray(serviceCfg.args) ? serviceCfg.args : [],
      env: { ...(serviceCfg.env || {}), ...envFromEditor },
    };
  }

  return {
    transport: state.transport || "stdio",
    command: state.command || "npx",
    args: Array.isArray(state.args) ? state.args : [],
    env: envFromEditor,
  };
};

const validatePrompt = (prompt, input_parameters) => {
  try {
    const promptArray = JSON.parse(prompt || "[]");
    if (!Array.isArray(promptArray)) return false;

    const inputKeys = new Set(Object.keys(input_parameters));
    return promptArray.every((item) => {
      if (item.type === "mention") {
        const id = item.attrs?.id || "";
        if (!id.startsWith("input_parameters.")) return false;
        const key = id.replace("input_parameters.", "");
        return inputKeys.has(key);
      }
      return true;
    });
  } catch {
    return false;
  }
};



export const handleRunAllMcp = async (
  nodes,
  edges,
  servicesData,
  input_parameters,
  setNodes,
  setNodeStatus,
  setAllResults,
  setAlert,
  isThinking
) => {
  const updateResults = (newResults) => {
    setAllResults((prev) => {
      const updated = { ...prev, ...newResults };
      console.log("Updated allResults:", JSON.stringify(updated, null, 2));
      return updated;
    });
  };

  setAllResults({});
  setNodeStatus(nodes.reduce((acc, node) => ({ ...acc, [node.id]: "idle" }), {}));
  try {
    const results = {};

    const nodeMap = new Map(
      nodes.map((node) => [
        node.id,
        { ...node, children: [] },
      ])
    );
    edges.forEach((edge) => {
      if (nodeMap.has(edge.source) && nodeMap.has(edge.target)) {
        nodeMap.get(edge.source).children.push(nodeMap.get(edge.target));
      }
    });


    const rootNodes = nodes.filter(
      (node) => !edges.some((edge) => edge.target === node.id)
    );
    console.log("🚀 ~ handleRunAllMcp ~ rootNodes:", rootNodes)
    let allNodes = []
    rootNodes.map((node)=>{
      if("data" in node){
        let data = node?.data
        let {args,env,command,prompt,transport,tool,name,service} = data
        let id = node.id
       
 let allPrompt = "";
      let jsonPrompt = JSON.parse(node.data?.prompt || "[]");
      
      jsonPrompt.forEach((k) => {
        if (k.type === "mention") {
          let attrId = k.attrs.id;
          if (attrId?.startsWith("input_parameters")) {
            let newKey = attrId.replace("input_parameters.", "");
            let value = input_parameters?.[newKey] || "";
            allPrompt += value;
          }
        } else {
          allPrompt += k.text;
        }
      });

        let obj = {
          args,env,command,transport,tool,name,service,prompt:allPrompt,id
        }

        allNodes.push(obj)
      }

    })

    if(isThinking){
      console.log("🚀 ~ handleRunAllMcp ~ response:", allNodes)
      const response = await postJson("/api/run_all_mcp", {configurations:allNodes})

      const nodeResult = {
            id:1,
            name: "Thinking",
            result: response,
            children: [],
          };
  
          results["Thinking"] = nodeResult;
          updateResults({ ["Thinking"]: nodeResult });
    }else{

      
      const executeNode = async (node, parentId = null) => {
        console.log("🚀 ~ executeNode ~ Processing node:", node.id, node.data.name);
  
        if (!validatePrompt(node.data.prompt, input_parameters)) {
          setNodeStatus((prev) => ({ ...prev, [node.id]: "failed" }));
          setNodes((nds) =>
            nds.map((n) =>
              n.id === node.id ? { ...n, data: { ...n.data, status: "failed" } } : n
            )
          );
          const errorResult = {
            id: node.id,
            name: node.data.name,
            error: `Invalid prompt: mentions must start with 'input_parameters.' and match input_parameters keys.`,
            children: [],
          };
          results[node.data.name] = errorResult;
          updateResults({ [node.data.name]: errorResult });
          return errorResult;
        }
  
        setNodeStatus((prev) => ({ ...prev, [node.id]: "running" }));
        setNodes((nds) =>
          nds.map((n) =>
            n.id === node.id ? { ...n, data: { ...n.data, status: "running" } } : n
          )
        );
  
        let allPrompt = "";
        let jsonPrompt = JSON.parse(node.data?.prompt || "[]");
        
        jsonPrompt.forEach((k) => {
          if (k.type === "mention") {
            let attrId = k.attrs.id;
            if (attrId?.startsWith("input_parameters")) {
              let newKey = attrId.replace("input_parameters.", "");
              let value = input_parameters?.[newKey] || "";
              allPrompt += value;
            }
          } else {
            allPrompt += k.text;
          }
        });
  
        const effective = buildEffectiveConfig(servicesData, node.data);
        const dataToSubmit = {
          name: node.data.name,
          service: node.data.service,
          tool: node.data.tool,
          prompt: allPrompt,
          transport: effective.transport,
          command: effective.command,
          args: effective.args,
          env: effective.env,
          payload: node.data.payload,
          input_parameters,
          thinkMode: node.data.thinkMode || false,
        };
  
        try {
          const response = await postJson("/api/run_single_mcp", dataToSubmit);
          setNodeStatus((prev) => ({ ...prev, [node.id]: "completed" }));
          setNodes((nds) =>
            nds.map((n) =>
              n.id === node.id ? { ...n, data: { ...n.data, status: "completed" } } : n
            )
          );
  
          const nodeResult = {
            id: node.id,
            name: node.data.name,
            result: node.data.thinkMode ? response.text : response,
            children: [],
          };
  
          results[node.data.name] = nodeResult;
          updateResults({ [node.data.name]: nodeResult });
  
          const children = Array.isArray(node.children) ? node.children : [];
          for (const child of children) {
            const childResult = await executeNode(child, node.id);
            nodeResult.children.push(childResult);
            results[node.data.name] = nodeResult; // Update parent with children
            updateResults({ [node.data.name]: nodeResult });
          }
  
          if (parentId && results[parentId]) {
            results[parentId].children.push(nodeResult);
            updateResults({ [parentId]: results[parentId] });
          }
  
          return nodeResult;
        } catch (error) {
          console.error(`Error executing node ${node.id}:`, error);
          let message = error.message.includes("HTTP error")
            ? error.message
            : `Failed to execute node "${node.data.name}". ${error.message}`;
          if (node.data.service === "Add Mcp") {
            message += " Please verify your environment variables and command arguments.";
          }
          setNodeStatus((prev) => ({ ...prev, [node.id]: "failed" }));
          setNodes((nds) =>
            nds.map((n) =>
              n.id === node.id ? { ...n, data: { ...n.data, status: "failed" } } : n
            )
          );
  
          const errorResult = {
            id: node.id,
            name: node.data.name,
            error: message,
            children: [],
          };
  
          results[node.data.name] = errorResult;
          updateResults({ [node.data.name]: errorResult });
  
          const children = Array.isArray(node.children) ? node.children : [];
          for (const child of children) {
            const childResult = await executeNode(child, node.id);
            errorResult.children.push(childResult);
            results[node.data.name] = errorResult;
            updateResults({ [node.data.name]: errorResult });
          }
  
          if (parentId && results[parentId]) {
            results[parentId].children.push(errorResult);
            updateResults({ [parentId]: results[parentId] });
          }
  
          return errorResult;
        }
      };
  
      const executionPromises = rootNodes.map((node) => executeNode(node));
      await Promise.all(executionPromises);
  
      const visitedNodes = new Set();
      const collectVisitedNodes = (result) => {
        visitedNodes.add(result.id);
        result.children.forEach(collectVisitedNodes);
      };
      Object.values(results).forEach(collectVisitedNodes);
  
      const disconnectedNodesList = nodes.filter(
        (node) => !visitedNodes.has(node.id)
      );
      for (const node of disconnectedNodesList) {
        const result = await executeNode(node);
        results[node.data.name] = result;
        updateResults({ [node.data.name]: result });
      }
    }

    setAlert({
      open: true,
      message: "All nodes executed successfully. Results stored with node names as keys.",
      severity: "success",
      title: "Execution Completed",
    });
  } catch (error) {
    console.error("Error in handleRunAllMcp:", error);
    setAlert({
      open: true,
      message: `Failed to execute all nodes: ${error.message}. Please check your environment variables, arguments, or server connection.`,
      severity: "error",
      title: "Execution Failed",
    });
  } finally {
    setNodeStatus(nodes.reduce((acc, node) => ({ ...acc, [node.id]: "idle" }), {}));
    setNodes((nds) =>
      nds.map((n) => ({ ...n, data: { ...n.data, status: "idle" } }))
    );
  }
};

const MCPWorkflow = ({
  final_resulted_json,
  handleSaveAlljson,
  ll_data = {},
  setAllMcp = () => {},
  previousNodes = null,
  thinkingStatus=false
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [allMergedData, setallMergedData] = useState({});
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [servicesData, setServicesData] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editNodeId, setEditNodeId] = useState(null);
  const [isEditClicked, setIsEditClicked] = useState(null);
  const [isThinking, setisThinking] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    service: null,
    tool: null,
    prompt: "",
    transport: "stdio",
    command: "npx",
    env: JSON.stringify({}, null, 2),
    args: [""],
    payload: JSON.stringify({}, null, 2),
    thinkMode: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
    title: "",
  });
  const [allResults, setAllResults] = useState({});
  const [connectedNodes, setConnectedNodes] = useState([]);
  const [disconnectedNodes, setDisconnectedNodes] = useState([]);
  const [input_parameters, setinput_parameters] = useState({});
  const [nodeStatus, setNodeStatus] = useState({});

  const generatePromptFromInputParameters = () => {
    const keys = Object.keys(input_parameters);
    if (keys.length > 0) {
      let allKeys = [];
      let counter = 0;
      for (let key of keys) {
        counter++;
        let obj = {
          type: "mention",
          attrs: {
            id: `input_parameters.${key}`,
            label: `input_parameters.${key} `,
            mentionSuggestionChar: "@",
          },
        };
        let obj2 = {
          type: "text",
          text: ", ",
        };
        allKeys.push(obj);
        if (counter !== keys?.length) allKeys.push(obj2);
      }
      return JSON.stringify(allKeys);
    }
    return "";
  };
useEffect(()=>{
  if(thinkingStatus){

    setisThinking(thinkingStatus)
  }
},[])


  useEffect(() => {
    if (previousNodes && Array.isArray(previousNodes)) {
      const newNodes = previousNodes.map((node, index) => ({
        id: node.id || `${index + 1}`,
        type: "custom",
        data: {
          name: node.name || `Node ${index + 1}`,
          isData: true,
          path: node.path || [],
          service: node.service || null,
          tool: node.tool || null,
          prompt: node.prompt || generatePromptFromInputParameters(),
          transport: node.transport || "stdio",
          command: node.command || "npx",
          env: node.env || {},
          args: node.args || [""],
          payload: node.payload || {},
          servicesData: servicesData,
          availableTools: node.availableTools || [],
          status: node.status || "idle",
          thinkMode: node.thinkMode || false,
          onTest: (path) => console.log("Test:", path),
          onEditClick: (event, id) => handleEditClick(event, id),
          onEdit: (updatedData) => {
            setNodes((nds) =>
              nds.map((n) =>
                n.id === updatedData.id
                  ? { ...n, data: { ...n.data, ...updatedData } }
                  : n
              )
            );
          },
          onCopy: (path) => console.log("Copy:", path),
          onRemove: (id) => {
            setNodes((nds) => nds.filter((n) => n.id !== id));
            setEdges((eds) =>
              eds.filter((edge) => edge.source !== id && edge.target !== id)
            );
          },
          onSubmit: (nodeData) => handleSubmit(nodeData),
          onConnect: (nodeData) => handleConnect(nodeData),
          onThinkModeChange: (id, checked) => {
            setNodes((nds) =>
              nds.map((n) =>
                n.id === id ? { ...n, data: { ...n.data, thinkMode: checked } } : n
              )
            );
          },
        },
        position: node.position || { x: 250 + index * 50, y: 150 + index * 50 },
      }));
      setNodes(newNodes);
    } else {
      setNodes([
        {
          id: "1",
          type: "custom",
          data: {
            name: "Main API",
            isData: true,
            path: [],
            service: null,
            tool: null,
            prompt: "",
            transport: "stdio",
            command: "npx",
            env: {},
            args: [""],
            payload: {},
            servicesData: [],
            availableTools: [],
            status: "idle",
            thinkMode: false,
            onTest: (path) => console.log("Test:", path),
            onEditClick: (event, id) => handleEditClick(event, id),
            onEdit: (updatedData) => {
              setNodes((nds) =>
                nds.map((node) =>
                  node.id === updatedData.id
                    ? { ...node, data: { ...node.data, ...updatedData } }
                    : node
                )
              );
            },
            onCopy: (path) => console.log("Copy:", path),
            onRemove: (id) => {
              setNodes((nds) => nds.filter((node) => node.id !== id));
              setEdges((eds) =>
                eds.filter((edge) => edge.source !== id && edge.target !== id)
              );
            },
            onSubmit: (nodeData) => handleSubmit(nodeData),
            onConnect: (nodeData) => handleConnect(nodeData),
            onThinkModeChange: (id, checked) => {
              setNodes((nds) =>
                nds.map((n) =>
                  n.id === id ? { ...n, data: { ...n.data, thinkMode: checked } } : n
                )
              );
            },
          },
          position: { x: 250, y: 25 },
        },
      ]);
    }
  }, [previousNodes, servicesData]);

  useEffect(() => {
    if (final_resulted_json?.apiResponse || ll_data) {
      const properties = ll_data?.answer?.properties || {};
      let all_llm_data = {};
      all_llm_data = schemaToEndParamsFlat({ properties });
      setinput_parameters({
        ...all_llm_data,
      });
    }
  }, [ll_data]);

  useEffect(() => {
    if (Object.keys(allResults).length > 0) {
      handleSaveAlljson({ apiResponse: limitArrayElements(allResults, 2) ,isThinking});
    }
  }, [allResults]);


  const getServiceToolConfig = (services, serviceName) => {
    if (!serviceName) return null;
    return services?.find((s) => s.service === serviceName)?.tool_config || null;
  };

  const mergeEnv = (baseEnvObj, overrideEnvObj) => {
    return { ...(baseEnvObj || {}), ...(overrideEnvObj || {}) };
  };

  useEffect(() => {
    const nodeIdsInEdges = new Set([
      ...edges.map((edge) => edge.source),
      ...edges.map((edge) => edge.target),
    ]);
    const connected = nodes.filter((node) => nodeIdsInEdges.has(node.id));
    const disconnected = nodes.filter((node) => !nodeIdsInEdges.has(node.id));
    setConnectedNodes(connected);
    setDisconnectedNodes(disconnected);
  }, [nodes, edges]);

  useEffect(() => {
    if (isEditClicked) {
      const node = nodes.find((n) => n.id === isEditClicked);
      if (node) {
        setEditNodeId(isEditClicked);
        const prompt = node.data.prompt || generatePromptFromInputParameters();
        setEditData({
          id: isEditClicked,
          name: node.data.name || "",
          service: node.data.service || null,
          tool: node.data.tool || null,
          prompt,
          transport: node.data.transport || "stdio",
          command: node.data.command || "npx",
          env:
            node.data.env && Object.keys(node.data.env).length > 0
              ? JSON.stringify(node.data.env, null, 2)
              : JSON.stringify({}, null, 2),
          args:
            node.data.args && node.data.args.length > 0
              ? [...node.data.args]
              : [""],
          payload:
            node.data.payload && Object.keys(node.data.payload).length > 0
              ? JSON.stringify(node.data.payload, null, 2)
              : JSON.stringify({}, null, 2),
          thinkMode: node.data.thinkMode || false,
        });
      }
    }
  }, [isEditClicked, nodes, input_parameters]);

  useEffect(() => {
    if (ll_data) {
      setallMergedData((prev) => ({ ...prev, ...ll_data }));
    }
  }, [ll_data]);

  const handleEditClick = (event, nodeId) => {
    setIsEditClicked(nodeId);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setEditNodeId(null);
    setIsEditClicked(null);
    setEditData({
      name: "",
      service: null,
      tool: null,
      prompt: "",
      transport: "stdio",
      command: "npx",
      env: JSON.stringify({}, null, 2),
      args: [""],
      payload: JSON.stringify({}, null, 2),
      thinkMode: false,
    });
  };

  const handleAlertClose = () => {
    setAlert({ ...alert, open: false });
  };

  const handleSave = () => {
    try {
      const node = nodes.find((n) => n.id === editNodeId);
      if (!node) return;

      const updatedData = {
        ...node.data,
        ...editData,
        env: safeParseObj(editData.env, {}),
        payload: safeParseObj(editData.payload || "{}", {}),
        id: editNodeId,
        thinkMode: editData.thinkMode,
      };
      node.data.onEdit(updatedData);
      handleClose();
    } catch (error) {
      console.error("Invalid JSON in env or payload:", error);
      setAlert({
        open: true,
        message: "Invalid JSON in environment variables or payload. Please ensure all inputs are valid JSON.",
        severity: "error",
        title: "Configuration Error",
      });
    }
  };

  const handleAddArg = () => {
    setEditData({ ...editData, args: [...editData.args, ""] });
  };

  const handleArgChange = (index, value) => {
    setEditData({
      ...editData,
      args: editData.args.map((arg, i) => (i === index ? value : arg)),
    });
  };

  const handleRemoveArg = (index) => {
    setEditData({
      ...editData,
      args: editData.args.filter((_, i) => i !== index),
    });
  };

  const handleServiceChange = (e, newValue) => {
    const prompt = editData.prompt || generatePromptFromInputParameters();
    if (newValue === "Add Mcp") {
      setEditData((prev) => ({
        ...prev,
        service: newValue,
        tool: null,
        prompt,
        payload: pretty({ env: safeParseObj(prev.env || "{}", {}) }),
        transport: "stdio",
        command: "npx",
        args: ["slack-mcp-server@latest"],
        env: prev.env || pretty({}),
        thinkMode: prev.thinkMode || false,
      }));
      return;
    }

    const cfg = getServiceToolConfig(servicesData, newValue) || {};
    const mergedEnv = mergeEnv(cfg.env || {}, safeParseObj(editData.env || "{}", {}));
    setEditData((prev) => ({
      ...prev,
      service: newValue,
      tool: null,
      prompt,
      payload: pretty({ env: mergedEnv }),
      transport: cfg.transport || "stdio",
      command: cfg.command || "npx",
      args: Array.isArray(cfg.args) ? cfg.args : [],
      env: pretty(mergedEnv),
      thinkMode: prev.thinkMode || false,
    }));
  };

  const generatePayloadFromSchema = (
    toolName,
    servicesData,
    availableTools,
    envAsString
  ) => {
    const parsedEnv = safeParseObj(envAsString || "{}", {});
    const tool =
      servicesData
        .find((s) => s.service === editData.service)
        ?.tools.find((t) => t.name === toolName) ||
      availableTools.find((t) => t.name === toolName);

    const payload = { env: parsedEnv };
    if (!tool || !tool.payload_schema) return payload;

    const generateNestedObject = (schema) => {
      const result = {};
      schema.forEach((field) => {
        const { type, field: fieldName, properties } = field;
        if (type === "object" && properties) {
          result[fieldName] = generateNestedObject(properties);
        } else {
          switch (type) {
            case "string":
              result[fieldName] = "";
              break;
            case "number":
              result[fieldName] = fieldName === "limit" ? 100 : 0;
              break;
            case "boolean":
              result[fieldName] = false;
              break;
            case "array":
              result[fieldName] = [];
              break;
            default:
              result[fieldName] = null;
          }
        }
      });
      return result;
    };

    tool.payload_schema.forEach((field) => {
      const { type, field: fieldName, properties } = field;
      if (type === "object" && properties) {
        payload[fieldName] = generateNestedObject(properties);
      } else {
        switch (type) {
          case "string":
            payload[fieldName] = "";
            break;
          case "number":
            payload[fieldName] = fieldName === "limit" ? 100 : 0;
            break;
          case "boolean":
            payload[fieldName] = false;
            break;
          case "array":
            payload[fieldName] = [];
            break;
          default:
            payload[fieldName] = null;
        }
      }
    });

    return payload;
  };

  const handleToolChange = (e, newValue) => {
    const toolName = newValue?.value || null;
    const payload = toolName
      ? generatePayloadFromSchema(
          toolName,
          servicesData,
          availableTools,
          editData.env
        )
      : { env: safeParseObj(editData.env || "{}", {}) };

    setEditData({
      ...editData,
      tool: toolName,
      prompt: toolName ? "" : editData.prompt,
      payload: pretty(payload),
      thinkMode: editData.thinkMode || false,
    });
  };

  const handleEnvChange = (newEnvString) => {
    setEditData((prev) => {
      let nextPayload = prev.payload;
      try {
        const payloadObj = safeParseObj(prev.payload || "{}", {});
        const envObj = safeParseObj(newEnvString || "{}", {});
        nextPayload = pretty({ ...payloadObj, env: envObj });
      } catch {}
      return { ...prev, env: newEnvString, payload: nextPayload, thinkMode: prev.thinkMode };
    });
  };

  const makeSubmissionEnvelope = (services, nodeState) => {
    const effective = buildEffectiveConfig(services, nodeState);
    return {
      name: nodeState.name,
      service: nodeState.service,
      tool: nodeState.tool,
      prompt: nodeState.prompt,
      transport: effective.transport,
      command: effective.command,
      args: effective.args,
      env: effective.env,
      payload: typeof nodeState.payload === "string" ? safeParseObj(nodeState.payload || "{}", {}) : nodeState.payload || {},
      tool_config: effective.tool_config,
      effective_source: effective.source,
      thinkMode: nodeState.thinkMode || false,
    };
  };

  const handleSubmit = async (nodeData) => {
    setIsLoading(true);
    try {
      if (!validatePrompt(nodeData.prompt, input_parameters)) {
        setNodeStatus((prev) => ({ ...prev, [nodeData.id]: "failed" }));
        setAlert({
          open: true,
          message: `Invalid prompt for node "${nodeData.name}": mentions must start with 'input_parameters.' and match input_parameters keys.`,
          severity: "error",
          title: "Prompt Validation Failed",
        });
        return;
      }

      let allPrompt = "";
      let jsonPrompt = JSON.parse(nodeData.prompt || "[]");
      
      jsonPrompt.forEach((k) => {
        if (k.type === "mention") {
          let attrId = k.attrs.id;
          if (attrId?.startsWith("input_parameters")) {
            let newKey = attrId.replace("input_parameters.", "");
            let value = input_parameters?.[newKey] || "";
            allPrompt += value;
          }
        } else {
          allPrompt += k.text;
        }
      });

      const effective = buildEffectiveConfig(servicesData, nodeData);
      const dataToSubmit = {
        name: nodeData.name,
        service: nodeData.service,
        tool: nodeData.tool,
        prompt: allPrompt,
        transport: effective.transport,
        command: effective.command,
        args: effective.args,
        env: effective.env,
        payload: nodeData.payload,
        input_parameters,
        thinkMode: nodeData.thinkMode || false,
      };

      setNodeStatus((prev) => ({ ...prev, [nodeData.id]: "running" }));
      const result = await postJson("/api/run_single_mcp", dataToSubmit);
      setNodeStatus((prev) => ({ ...prev, [nodeData.id]: "completed" }));

      setAllResults((prev) => {
        const updated = {
          ...prev,
          [nodeData.name]: {
            id: nodeData.id,
            name: nodeData.name,
            result: nodeData.thinkMode ? result.text : result,
            children: [],
          },
        };
        console.log("Updated allResults from handleSubmit:", JSON.stringify(updated, null, 2));
        return updated;
      });

      setAlert({
        open: true,
        message: `Node "${nodeData.name}" executed successfully.`,
        severity: "success",
        title: "Execution Successful",
      });
      return nodeData.thinkMode ? result.text : result;
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setNodeStatus((prev) => ({ ...prev, [nodeData.id]: "failed" }));
      let message = error.message.includes("HTTP error")
        ? error.message
        : `Failed to execute node "${nodeData.name}". ${error.message}`;
      if (nodeData.service === "Add Mcp") {
        message += " Please ensure environment variables and arguments are correctly formatted.";
      }
      setAllResults((prev) => {
        const updated = {
          ...prev,
          [nodeData.name]: {
            id: nodeData.id,
            name: nodeData.name,
            error: message,
            children: [],
          },
        };
        console.log("Updated allResults from handleSubmit error:", JSON.stringify(updated, null, 2));
        return updated;
      });
      setAlert({
        open: true,
        message,
        severity: "error",
        title: "Execution Failed",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (nodeData) => {
    try {
      const effective = buildEffectiveConfig(servicesData, nodeData);
      const dataToSubmit = {
        name: nodeData.name,
        service: nodeData.service,
        tool: nodeData.tool,
        prompt: nodeData.prompt,
        transport: effective.transport,
        command: effective.command,
        args: effective.args,
        env: effective.env,
        payload: safeParseObj(nodeData.payload || "{}", {}),
        thinkMode: nodeData.thinkMode || false,
      };

      const response = await postJson("/api/get_single_mcp_tools", dataToSubmit);
      const responseData = response?.responseData?.[0];

      if (!responseData?.tools) {
        console.warn("No tools found in responseData");
        setAlert({
          open: true,
          message: "No tools found in response. Please verify the service configuration or try reconnecting.",
          severity: "warning",
          title: "Connection Warning",
        });
        return;
      }

      const payloadObj = generatePayloadFromSchema(
        nodeData.tool,
        [],
        responseData.tools,
        nodeData.env
      );
      const newPayload = pretty(payloadObj);

      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeData.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  availableTools: responseData.tools || [],
                  payload: newPayload,
                  thinkMode: nodeData.thinkMode || false,
                },
              }
            : node
        )
      );

      if (editNodeId === nodeData.id) {
        setEditData((prev) => ({ ...prev, payload: newPayload, thinkMode: nodeData.thinkMode }));
      }

      setAlert({
        open: true,
        message: `Node "${nodeData.name}" connected successfully, payload updated.`,
        severity: "success",
        title: "Connection Successful",
      });
    } catch (error) {
      console.error("Error in handleConnect:", error);
      let message = error.message.includes("HTTP error")
        ? error.message
        : `Failed to connect node "${nodeData.name}". ${error.message}`;
      if (nodeData.service === "Add Mcp") {
        message += " Please check your environment variables or command arguments.";
      }
      setAlert({
        open: true,
        message,
        severity: "error",
        title: "Connection Failed",
      });
    }
  };

  const onConnect = (params) => {
    const newEdge = {
      id: `${params.source}-${params.target}`,
      source: params.source,
      target: params.target,
      animated: true,
      style: { stroke: "#4d35ff" },
    };
    setEdges((eds) => [...eds, newEdge]);
  };

  const fetchServices = async () => {
    try {
      const response = await fetch("/api/mcp_tools");
      const data = await response.json();
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}. Please check your server connection.`);
      }
      setServicesData(data);
      setNodes((nodes) =>
        nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            servicesData: data,
            onEditClick: (event, id) => handleEditClick(event, id),
          },
        }))
      );
      setAlert({
        open: true,
        message: "MCP services fetched successfully.",
        severity: "success",
        title: "Services Loaded",
      });
    } catch (error) {
      console.error("Error fetching services:", error);
      setServicesData([]);
      setAlert({
        open: true,
        message: `Failed to fetch MCP services: ${error.message}. Please check your server connection or API endpoint.`,
        severity: "error",
        title: "Fetch Failed",
      });
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    mergeNodeData();
  }, [nodes]);

  const mergeNodeData = () => {
    let newNodes = JSON.parse(JSON.stringify(nodes));
    const mergedData = {
      nodes: newNodes.map((node) => ({
        id: node.id,
        name: node.data.name,
        service: node.data.service,
        tool: node.data.tool,
        prompt: node.data.prompt,
        transport: node.data.transport,
        command: node.data.command,
        args: node.data.args,
        env: node.data.env,
        payload: node.data.payload,
        position: node.position,
        thinkMode: node.data.thinkMode,
      })),
      edges: edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
      })),
    };
    setAllMcp(mergedData.nodes);
  };

  const addNode = () => {
    const newNodeId = `${nodes.length + 1}`;
    const newNode = {
      id: newNodeId,
      type: "custom",
      data: {
        name: `Node ${newNodeId}`,
        isData: true,
        path: [],
        service: null,
        tool: null,
        prompt: generatePromptFromInputParameters(),
        transport: "stdio",
        command: "npx",
        env: {},
        args: [""],
        payload: {},
        servicesData: servicesData,
        availableTools: [],
        status: "idle",
        thinkMode: false,
        onTest: (path) => console.log("Test:", path),
        onEditClick: (event, id) => handleEditClick(event, id),
        onEdit: (updatedData) => {
          setNodes((nds) =>
            nds.map((node) =>
              node.id === updatedData.id
                ? { ...node, data: { ...node.data, ...updatedData } }
                : node
            )
          );
        },
        onCopy: (path) => console.log("Copy:", path),
        onRemove: (id) => {
          setNodes((nds) => nds.filter((node) => node.id !== id));
          setEdges((eds) =>
            eds.filter((edge) => edge.source !== id && edge.target !== id)
          );
        },
        onSubmit: (nodeData) => handleSubmit(nodeData),
        onConnect: (nodeData) => handleConnect(nodeData),
        onThinkModeChange: (id, checked) => {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id ? { ...n, data: { ...n.data, thinkMode: checked } } : n
            )
          );
        },
      },
      position: { x: 250 + nodes.length * 50, y: 150 + nodes.length * 50 },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const selectedService = servicesData?.length
    ? servicesData.find((s) => s.service === editData.service)
    : null;

  const availableTools = selectedService
    ? selectedService.tools.map((t) => ({
        label: t.name,
        value: t.name,
        description: t.description,
      }))
    : (nodes.find((n) => n.id === editNodeId)?.data.availableTools || []).map(
        (t) => ({
          label: t.name,
          value: t.name,
          description: t.description,
          ...t,
        })
      );

  return (
    <div
      style={{
        width: "100%",
        height: "600px",
        background: "#1A1C37",
        borderRadius: "8px",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: "10px",
          padding: "10px",
          position: "absolute",
          top: "0",
          left: "0",
          zIndex: 1000,
        }}
      >
        <Button
          type="button"
          variant="contained"
          onClick={fetchServices}
          style={{ backgroundColor: "#4d35ff" }}
        >
          Fetch MCP
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={addNode}
          style={{ backgroundColor: "#4d35ff" }}
        >
          Add Node
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={() => {
            setIsRunningAll(true);
            handleRunAllMcp(
              nodes,
              edges,
              servicesData,
              input_parameters,
              setNodes,
              setNodeStatus,
              setAllResults,
              setAlert,
              isThinking
            ).finally(() => setIsRunningAll(false));
          }}
          disabled={isRunningAll}
          style={{ backgroundColor: "#4d35ff" }}
        >
          {isRunningAll ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Run All MCP"
          )}
        </Button>


        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              checked={isThinking || false}
              onChange={(e) => setisThinking(e.target.checked )}
              style={{ cursor: "pointer" }}
            />
            <Typography style={{ fontSize: "12px", color: "#ccc" }}>
              Think Mode (Enhances result accuracy, returns text only)
            </Typography>
          </div>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        style={{ background: "#1A1C37", flex: 1 }}
      >
        <Controls />
        <Background variant="dots" gap={12} />
      </ReactFlow>
      <Typography variant="h5" style={{ marginTop: "20px", color: "white" }}>
        Edit your Input Parameters values:
      </Typography>
      <pre
        style={{
          background: "#f9f9f9",
          padding: "0",
          maxHeight: "300px",
          overflow: "auto",
          color: "#333",
        }}
      >
        <JsonViewer
          jsonData={input_parameters}
          onChange={(data) => {
            setinput_parameters(data);
          }}
          editable={false}
        />
      </pre>
      <div
        style={{
          background: "#2A2C4B",
          padding: "10px",
          borderTop: "1px solid #4d35ff",
          maxHeight: "200px",
          overflowY: "auto",
          color: "white",
        }}
      >
        <Typography variant="h6" style={{ color: "white" }}>
          Execution Results
        </Typography>
        <pre
          style={{
            fontSize: "12px",
            whiteSpace: "pre-wrap",
            background: "white",
          }}
        >
          {JSON.stringify(allResults, null, 2)}
        </pre>
      </div>
      <Popover
        id={editNodeId ? "edit-popover" : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
        PaperProps={{
          style: {
            backgroundColor: "#1A1C37",
            color: "white",
            width: "560px",
            maxHeight: "calc(100vh - 100px)",
            border: "1px solid black",
            overflowY: "auto",
          },
        }}
      >
        <Box p={2} sx={{ width: "100%", background: "#1A1C37", color: "white" }}>
          <Typography variant="h6" gutterBottom sx={{ color: "white" }}>
            Edit Node
          </Typography>
          <TextField
            fullWidth
            label="Name"
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            margin="normal"
            size="small"
            InputLabelProps={{ style: { color: "white" } }}
            InputProps={{ style: { color: "white" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#4d35ff" },
                "&:hover fieldset": { borderColor: "#4d35ff" },
                "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
              },
            }}
          />
          <Autocomplete
            options={[
              ...(servicesData?.length ? servicesData.map((s) => s.service) : []),
              "Add Mcp",
            ]}
            value={editData.service}
            onChange={handleServiceChange}
            renderOption={(props, option) => (
              <li
                key={JSON.stringify(option)}
                {...props}
                className={"mcp_list " + props?.className}
                style={{
                  ...props.style,
                  backgroundColor: option === "Add Mcp" ? "#2A2C4B" : "inherit",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {option === "Add Mcp" && (
                  <AddIcon sx={{ color: "white", marginRight: 1, fontSize: 16 }} />
                )}
                {option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Service"
                margin="normal"
                size="small"
                InputLabelProps={{ style: { color: "white" } }}
                InputProps={{
                  ...params.InputProps,
                  style: { color: "white" },
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <div style={{ color: "white" }}>&nbsp;</div>
                    </>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#4d35ff" },
                    "&:hover fieldset": { borderColor: "#4d35ff" },
                    "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
                  },
                  "& .MuiAutocomplete-popupIndicator": { color: "white" },
                  "& .MuiAutocomplete-clearIndicator": { color: "white" },
                }}
              />
            )}
            PopperProps={{ style: { backgroundColor: "#1A1C37", color: "white" } }}
            ListboxProps={{
              style: {
                backgroundColor: "#1A1C37",
                color: "white",
              },
            }}
          />
          {/* <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              checked={editData.thinkMode || false}
              onChange={(e) => setEditData({ ...editData, thinkMode: e.target.checked })}
              style={{ cursor: "pointer" }}
            />
            <Typography style={{ fontSize: "12px", color: "#ccc" }}>
              Think Mode (Enhances result accuracy, returns text only)
            </Typography>
          </div> */}
          {editData.service === "Add Mcp" && (
            <>
              {editData.args.map((arg, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1} mt={1}>
                  <TextField
                    fullWidth
                    label={`Arg ${editData.args?.length ? index + 1 : ""}`}
                    value={arg}
                    onChange={(e) => handleArgChange(index, e.target.value)}
                    size="small"
                    InputLabelProps={{ style: { color: "white" } }}
                    InputProps={{ style: { color: "white" } }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#4d35ff" },
                        "&:hover fieldset": { borderColor: "#4d35ff" },
                        "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
                      },
                    }}
                  />
                  {editData.args.length > 1 && (
                    <IconButton
                      onClick={() => handleRemoveArg(index)}
                      style={{ color: "white", padding: 0 }}
                    >
                      <IoMdClose size={16} />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                type="button"
                variant="outlined"
                onClick={handleAddArg}
                size="small"
                sx={{ mt: 1, color: "white", borderColor: "#4d35ff" }}
              >
                Add More Arg
              </Button>
            </>
          )}
          <Autocomplete
            options={availableTools}
            getOptionLabel={(option) => option.label}
            value={availableTools.find((t) => t.value === editData.tool) || null}
            onChange={handleToolChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tool"
                margin="normal"
                size="small"
                InputLabelProps={{ style: { color: "white" } }}
                InputProps={{
                  ...params.InputProps,
                  style: { color: "white" },
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <div style={{ color: "white" }}>&nbsp;</div>
                    </>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#4d35ff" },
                    "&:hover fieldset": { borderColor: "#4d35ff" },
                    "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
                  },
                  "& .MuiAutocomplete-popupIndicator": { color: "white" },
                  "& .MuiAutocomplete-clearIndicator": { color: "white" },
                }}
              />
            )}
            PopperProps={{ style: { backgroundColor: "#1A1C37", color: "white" } }}
            ListboxProps={{
              style: {
                backgroundColor: "#1A1C37",
                color: "white",
              },
            }}
          />
          <TextField
            fullWidth
            label="Prompt"
            value={editData.prompt}
            onChange={(e) => setEditData({ ...editData, prompt: e.target.value })}
            margin="normal"
            size="small"
            multiline
            rows={3}
            disabled={!!editData.tool}
            InputLabelProps={{ style: { color: "white" } }}
            InputProps={{ style: { color: "white" } }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#4d35ff" },
                "&:hover fieldset": { borderColor: "#4d35ff" },
                "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
              },
            }}
          />
          <Typography>Prompt</Typography>
          <CustomTextEditor
            prompt={editData.prompt}
            handleTitleChange={(e) =>
              setEditData({ ...editData, prompt: e.target.value })}
            sampleData={{ input_parameters }}
          />
          <EnvEditor value={editData.env} onChange={handleEnvChange} />
          {editData.tool && (
            <TextField
              fullWidth
              label="Payload"
              value={editData.payload || "{}"}
              onChange={(e) => setEditData({ ...editData, payload: e.target.value })}
              margin="normal"
              size="small"
              multiline
              rows={4}
              error={(() => {
                try {
                  JSON.parse(editData.payload || "{}");
                  return false;
                } catch {
                  return true;
                }
              })()}
              helperText={(() => {
                try {
                  JSON.parse(editData.payload || "{}");
                  return "";
                } catch {
                  return "Invalid JSON";
                }
              })()}
              InputLabelProps={{ style: { color: "white" } }}
              InputProps={{ style: { color: "white" } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#4d35ff" },
                  "&:hover fieldset": { borderColor: "#4d35ff" },
                  "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
                },
                "& .MuiFormHelperText-root": { color: "white" },
              }}
            />
          )}
          {editData.service === "Add Mcp" && (
            <>
              <Autocomplete
                options={["stdio", "ipc", "tcp", "ws"]}
                value={editData.transport}
                onChange={(e, newValue) =>
                  setEditData({ ...editData, transport: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Transport"
                    margin="normal"
                    size="small"
                    InputLabelProps={{ style: { color: "white" } }}
                    InputProps={{
                      ...params.InputProps,
                      style: { color: "white" },
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <div style={{ color: "white" }}>&nbsp;</div>
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#4d35ff" },
                        "&:hover fieldset": { borderColor: "#4d35ff" },
                        "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
                      },
                      "& .MuiAutocomplete-popupIndicator": { color: "white" },
                      "& .MuiAutocomplete-clearIndicator": { color: "white" },
                    }}
                  />
                )}
                PopperProps={{ style: { backgroundColor: "#1A1C37", color: "white" } }}
                ListboxProps={{
                  style: { backgroundColor: "#1A1C37", color: "white" },
                }}
              />
              <Autocomplete
                options={["npx", "npm", "node", "yarn"]}
                value={editData.command}
                onChange={(e, newValue) =>
                  setEditData({ ...editData, command: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Command"
                    margin="normal"
                    size="small"
                    InputLabelProps={{ style: { color: "white" } }}
                    InputProps={{
                      ...params.InputProps,
                      style: { color: "white" },
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          <div style={{ color: "white" }}>&nbsp;</div>
                        </>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#4d35ff" },
                        "&:hover fieldset": { borderColor: "#4d35ff" },
                        "&.Mui-focused fieldset": { borderColor: "#4d35ff" },
                      },
                      "& .MuiAutocomplete-popupIndicator": { color: "white" },
                      "& .MuiAutocomplete-clearIndicator": { color: "white" },
                    }}
                  />
                )}
                PopperProps={{ style: { backgroundColor: "#1A1C37", color: "white" } }}
                ListboxProps={{
                  style: { backgroundColor: "#1A1C37", color: "white" },
                }}
              />
            </>
          )}
          
          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button
              type="button"
              onClick={handleClose}
              sx={{ color: "white", borderColor: "#4d35ff" }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleSave}
              disabled={
                editData.service === "Add Mcp" &&
                !(() => {
                  try {
                    JSON.parse(editData.env);
                    JSON.parse(editData.payload || "{}");
                    return true;
                  } catch {
                    return false;
                  }
                })()
              }
              sx={{ backgroundColor: "#4d35ff" }}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                const node = nodes.find((n) => n.id === editNodeId);
                if (node) {
                  node.data.onSubmit({ ...editData, id: editNodeId });
                }
              }}
              disabled={isLoading}
              sx={{ backgroundColor: "#4d35ff" }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Run"
              )}
            </Button>
          </Box>
        </Box>
      </Popover>
      <AlertMessage
        open={alert.open}
        onClose={handleAlertClose}
        message={alert.message}
        severity={alert.severity}
        title={alert.title}
      />
    </div>
  );
};

export default MCPWorkflow;